import { useKaraoke } from '@src/hooks/useKaraoke';
import { useKaraokeStore, useUserIdentity } from '@src/stores-v2/StoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import {
  CAPTION_PREVIEW_SCRIM,
  useCaptionPreviewBackground,
} from '../captionPreviewStyles';
import { preparePresetsToDisplay } from '../GlobalKaraokePresetSelect';
import PresetPreviewText from '../PresetPreviewText';

import type { Preset } from '@src/components/textProcessor/TextBrandingService';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

export const CaptionsPreviewThumbnail = ({
  videoCreator,
  hideTextPreview = false,
  activeConfigId,
}: {
  videoCreator: IVideoCreatorStore;
  hideTextPreview?: boolean;
  activeConfigId?: string;
}) => {
  const userIdentity = useUserIdentity();
  const { creatorStudioCaptionsEnableArborTemplates } = useFlagsCombination(
    userIdentity.currentRole,
  );

  const { globalKaraokePresets } = videoCreator.textBrandingService;
  const aspectRatio = videoCreator.getCurrentAspectRatio();

  const presets = useMemo(
    preparePresetsToDisplay(globalKaraokePresets, aspectRatio),
    [globalKaraokePresets, aspectRatio],
  );

  // The same frame the style cards (`PresetPreview`) preview over, from the
  // same hook — so the panel and the Replace Captions modal cannot disagree.
  const thumbnailBackground = useCaptionPreviewBackground(videoCreator);

  const templates = (
    videoCreator.textBrandingService.brandingTemplates || []
  ).filter(t => {
    const templateId = typeof t.id === 'string' ? t.id : undefined;
    return !videoCreator.textBrandingService.isPresetTemplate(templateId);
  });

  const karaokeStore = useKaraokeStore();
  const { getKaraokeConfigForKaraokeClipId } = useKaraoke(
    videoCreator.rootStore.editorStore.editor,
  );

  const karaokeLine =
    videoCreator.rootStore.editorStore?.editor?.getKaraokeLines();

  const config =
    (karaokeLine?.[0]?.id
      ? getKaraokeConfigForKaraokeClipId(karaokeLine?.[0]?.id)
      : karaokeStore.defaultKaraokeConfig) ?? karaokeStore.defaultKaraokeConfig;

  // Find the current template/preset
  const allTemplates = [
    ...templates,
    ...(creatorStudioCaptionsEnableArborTemplates ? presets : []),
  ];

  const idToFind = activeConfigId || config?.parent_template_id;

  const currentTemplate = allTemplates.find(
    t => t?.id?.toString() === idToFind?.toString(),
  ) as Preset | undefined;

  if (hideTextPreview) {
    return <ThumbnailPlaceholder />;
  }

  if (!currentTemplate) {
    return (
      <ThumbnailWrapper backgroundUrl={thumbnailBackground}>
        <ThumbnailOverlay />
      </ThumbnailWrapper>
    );
  }

  return (
    <ThumbnailWrapper backgroundUrl={thumbnailBackground}>
      <ThumbnailOverlay />
      <PresetPreviewText template={currentTemplate} />
    </ThumbnailWrapper>
  );
};

const ThumbnailWrapper = styled.div<{ backgroundUrl?: string }>`
  width: 100%;
  height: 100%;
  border-radius: var(--radius);
  ${props =>
    props.backgroundUrl
      ? css`
          background-image: url(${props.backgroundUrl});
        `
      : css`
          background-image: url('/img/karaoke-preset-bg.png');
        `}
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

/** Keeps the caption text legible over a real video frame — see the scrim's doc. */
const ThumbnailOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${CAPTION_PREVIEW_SCRIM};
  border-radius: inherit;
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  border-radius: var(--radius);
  background-color: var(--surface);
`;
